import { motion } from "framer-motion";
import { Store, ExternalLink, Copy } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface DashboardStorefrontBannerProps {
  storefrontUrl: string;
  labels: {
    yourStorefront: string;
    openStorefront: string;
    copyUrl: string;
  };
}

export function DashboardStorefrontBanner({
  storefrontUrl,
  labels,
}: DashboardStorefrontBannerProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Mobile: bank-card style (blue gradient bg, white text)
  if (!isDesktop) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="rounded-2xl p-4"
        style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm text-white">{labels.yourStorefront}</p>
              <p className="text-xs text-white/70 truncate max-w-[140px]">
                {storefrontUrl.replace("https://", "")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigator.clipboard.writeText(storefrontUrl)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={labels.copyUrl}
            >
              <Copy className="w-4 h-4 text-white/80" />
            </button>
            <a
              href={storefrontUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-blue-700 text-sm font-semibold hover:bg-white/90 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {labels.openStorefront}
            </a>
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop: original card style
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.3 }}
      className="bg-card rounded-xl border border-border p-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-primary flex items-center justify-center">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-sm">{labels.yourStorefront}</p>
            <p className="text-xs text-muted-foreground truncate max-w-[140px] sm:max-w-[200px]">
              {storefrontUrl.replace("https://", "")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigator.clipboard.writeText(storefrontUrl)}
            className="p-2 rounded-sm hover:bg-primary/10 transition-colors"
            title={labels.copyUrl}
          >
            <Copy className="w-4 h-4 text-primary" />
          </button>
          <a
            href={storefrontUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {labels.openStorefront}
          </a>
        </div>
      </div>
    </motion.div>
  );
}
